import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { authAPI } from '../services/api.ts';
import {
  ActionIcon,
  Alert,
  Anchor,
  Box,
  Button,
  Center,
  Flex,
  Group,
  Image,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  ArrowRight,
  ListChecks,
  Lock,
  Mail,
  Moon,
  Smartphone,
  Sun,
  Users,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';

// What the panel is for, on the brand side of the page.
const POINTS = [
  { icon: Users, text: 'Manage users, roles and permissions' },
  { icon: ListChecks, text: 'Watch the Odoo queue and resend what failed' },
  { icon: Smartphone, text: 'See who is on the app and who is on the web' },
];

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await authAPI.login(email, password);
      if (response.token && response.user) {
        login(response);
        navigate('/dashboard');
      } else {
        setError('Login failed: Invalid response format');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex mih="100vh">
      {/* Brand side. Wide screens only. */}
      <Box
        visibleFrom="md"
        pos="relative"
        w="46%"
        maw={640}
        c="white"
        style={{
          overflow: 'hidden',
          background:
            'linear-gradient(150deg, var(--mantine-primary-color-6) 0%, var(--mantine-primary-color-9) 100%)',
        }}
      >
        {/* Soft shapes, for depth. */}
        <Box
          pos="absolute"
          top={-120}
          right={-120}
          w={360}
          h={360}
          style={{ borderRadius: '50%', background: 'rgba(255,255,255,0.10)' }}
        />
        <Box
          pos="absolute"
          bottom={-160}
          left={-100}
          w={420}
          h={420}
          style={{ borderRadius: '50%', background: 'rgba(255,255,255,0.07)' }}
        />

        <Stack pos="relative" h="100%" justify="space-between" p={56}>
          <Group gap="sm">
            <Center
              w={52}
              h={52}
              bg="white"
              style={{ borderRadius: 14, boxShadow: '0 6px 20px rgba(0,0,0,0.18)' }}
            >
              <Image src="/suncrop-logo.png" alt="" w={40} h={40} fit="contain" />
            </Center>
            <Text fw={700} size="lg">
              TeamSol Admin
            </Text>
          </Group>

          <Stack gap="xl">
            <Stack gap="sm">
              <Title order={1} fz={38} lh={1.15} c="white">
                Run your teams from one place
              </Title>
              <Text size="lg" opacity={0.85}>
                Users, projects, permissions and everything on its way to Odoo.
              </Text>
            </Stack>
            <Stack gap="md">
              {POINTS.map(({ icon: Icon, text }) => (
                <Group key={text} gap="sm" wrap="nowrap">
                  <Center
                    w={36}
                    h={36}
                    style={{ borderRadius: 10, background: 'rgba(255,255,255,0.16)', flexShrink: 0 }}
                  >
                    <Icon size={18} />
                  </Center>
                  <Text fw={500}>{text}</Text>
                </Group>
              ))}
            </Stack>
          </Stack>

          <Text size="sm" opacity={0.7}>
            Powered by TeamSol
          </Text>
        </Stack>
      </Box>

      {/* Form side. */}
      <Flex flex={1} direction="column">
        <Group justify="flex-end" p="md">
          <Tooltip label={theme === 'dark' ? 'Light mode' : 'Dark mode'}>
            <ActionIcon
              variant="default"
              size="lg"
              aria-label="Toggle theme"
              onClick={toggleTheme}
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </ActionIcon>
          </Tooltip>
        </Group>

        <Center flex={1} px="md" pb={64}>
          <Box w="100%" maw={400}>
            {/* On a phone the brand side is hidden, so the logo leads here. */}
            <Center hiddenFrom="md" mb="lg">
              <Image src="/suncrop-logo.png" alt="Logo" w={84} h={84} fit="contain" />
            </Center>

            <Stack gap={6} mb="xl">
              <Text tt="uppercase" c="dimmed" fw={600} size="xs" lts={1.5}>
                Admin sign in
              </Text>
              <Title order={2}>Welcome back</Title>
              <Text size="sm" c="dimmed">
                Sign in to your account to continue.
              </Text>
            </Stack>

            <form onSubmit={handleSubmit}>
              <Stack gap="md">
                {error && (
                  <Alert color="red" variant="light">
                    {error}
                  </Alert>
                )}

                <TextInput
                  id="email"
                  type="email"
                  label="Email address"
                  placeholder="you@company.com"
                  size="md"
                  autoComplete="username"
                  leftSection={<Mail size={18} />}
                  required
                  withAsterisk={false}
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                />

                <PasswordInput
                  id="password"
                  label="Password"
                  placeholder="Enter your password"
                  size="md"
                  autoComplete="current-password"
                  leftSection={<Lock size={18} />}
                  required
                  withAsterisk={false}
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  visible={showPassword}
                  onVisibilityChange={setShowPassword}
                />

                <Group justify="flex-end">
                  <Anchor component={Link} to="/forgot-password" size="sm" fw={500}>
                    Forgot password?
                  </Anchor>
                </Group>

                <Button
                  type="submit"
                  fullWidth
                  size="md"
                  loading={isLoading}
                  rightSection={<ArrowRight size={18} />}
                >
                  Sign in
                </Button>
              </Stack>
            </form>
          </Box>
        </Center>
      </Flex>
    </Flex>
  );
};
